<script lang="ts" context="module">
  export const bannerInfo = writable<{ rect: DOMRect | null; isSticky: boolean }>({
    rect: null,
    isSticky: false
  });
</script>

<script lang="ts">
  import { cn } from '$lib/utils';

  import type {
    BannerItemStoryblok,
    ConfigurationStoryblok,
    RegionalBannerItemStoryblok,
    RegionalCountdownBannerItemStoryblok,
    RegionalDailyCountdownBannerItemStoryblok
  } from '$types/bloks';
  import { region } from '$lib/region';
  import { getDeadline, padNumber, getDailyDeadline, type Deadline } from '$lib/utils/date';
  import { onMount } from 'svelte';
  import { validateRegex } from '$lib/utils/regex';
  import { page } from '$app/stores';
  import { slide } from 'svelte/transition';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { writable } from 'svelte/store';
  import { createInterval, createTimeout } from '$lib/stores/debounced';
  import Link from './ui/link.svelte';
  import { getAnchorFromCmsLink } from '$lib/utils/cms';
  import { cart } from '$lib/cart';
  import { getCampaignColors } from '$lib/stores/campaigns';

  export let items: ConfigurationStoryblok['banner_items'];
  export let isBannerSticky: ConfigurationStoryblok['is_sticky'];
  let className: string | undefined = undefined;
  export { className as class };

  interface RegionalDeadlineBannerItemStoryblok extends RegionalCountdownBannerItemStoryblok {
    deadlineFormatted: Deadline;
  }
  interface RegionalDailyDeadlineBannerItemStoryblok
    extends RegionalDailyCountdownBannerItemStoryblok {
    deadlineFormatted: Deadline;
  }

  let ref: HTMLElement;
  let bannerIndex = 0;
  let filteredBannerItems: (
    | BannerItemStoryblok
    | RegionalBannerItemStoryblok
    | RegionalDeadlineBannerItemStoryblok
    | RegionalDailyDeadlineBannerItemStoryblok
  )[] = [];

  const debounceTime = 200;
  let resizeTimeout: () => void;
  let scrollTimeout: () => void;

  $: url = $page.url.href;

  $: filteredRegionBannerItems = items.filter((item) => {
    return (
      (item.component === 'regional-banner-item' && item.regions?.includes($region)) ||
      item.component === 'banner-item' ||
      (item.component === 'regional-countdown-banner-item' && item.regions?.includes($region)) ||
      (item.component === 'regional-daily-countdown-banner-item' && item.regions?.includes($region))
    );
  });

  $: bannerItems = filteredRegionBannerItems.filter((item) => {
    let regexValidation = true;
    if (item.regex_type && item.regex) {
      regexValidation = validateRegex({ type: item.regex_type, string: url, pattern: item.regex });
    }

    let discountCodeValidation = true;

    const itemDiscountCodes = item.discount_codes
      ? item.discount_codes.split('\n').map((code: string) => code.toLowerCase())
      : [];
    if (itemDiscountCodes && itemDiscountCodes.length > 0) {
      let discountCodeOnCart = $cart?.discountCodes.find((code) =>
        itemDiscountCodes.includes(code.code.toLowerCase())
      );
      discountCodeValidation = Boolean(discountCodeOnCart);
    }

    return regexValidation && discountCodeValidation;
  });

  $: hasCountdownItems =
    filteredBannerItems.findIndex(
      (item) =>
        item.component === 'regional-countdown-banner-item' ||
        'regional-daily-countdown-banner-item'
    ) !== -1;

  const setFilteredBannerItems = () => {
    const exclusiveItem = bannerItems.find((item) => item.show_exclusively);

    const itemsToFilter = exclusiveItem ? [exclusiveItem] : bannerItems;

    filteredBannerItems = itemsToFilter
      .map((item) => {
        if (item.component === 'regional-countdown-banner-item' && item.deadline) {
          return {
            ...item,
            deadlineFormatted: getDeadline(item.deadline)
          } as RegionalDeadlineBannerItemStoryblok;
        }

        if (item.component === 'regional-daily-countdown-banner-item' && item.deadline) {
          const deadlineDate = new Date(item.deadline);
          return {
            ...item,
            deadlineFormatted: getDailyDeadline(
              `${deadlineDate.getHours()}:${deadlineDate.getMinutes()}`
            )
          } as RegionalDailyDeadlineBannerItemStoryblok;
        }

        return item as BannerItemStoryblok | RegionalBannerItemStoryblok;
      })
      .filter((item) => {
        if (item.component === 'regional-countdown-banner-item') {
          return !(
            item.deadlineFormatted.hours === 0 &&
            item.deadlineFormatted.minutes === 0 &&
            item.deadlineFormatted.seconds === 0
          );
        }
        return item !== undefined;
      });
  };

  const updateBannerInfo = () => {
    const rect = ref?.getBoundingClientRect();
    if (!rect) return;

    bannerInfo.update((prev) => {
      if (JSON.stringify(prev.rect) !== JSON.stringify(rect)) {
        return { ...prev, rect };
      }
      return prev;
    });
  };

  onMount(() => {
    if (ref) {
      updateBannerInfo();
    }

    const bannerUpdateCleanup = createInterval(() => {
      if (filteredBannerItems && filteredBannerItems.length > 0) {
        bannerIndex = (bannerIndex + 1) % filteredBannerItems.length;
      }
    }, 8000);

    const filterUpdateCleanup = createInterval(() => {
      setFilteredBannerItems();
    }, 1000);

    return () => {
      bannerUpdateCleanup();
      filterUpdateCleanup();
      if (resizeTimeout) resizeTimeout();
      if (scrollTimeout) scrollTimeout();
    };
  });
</script>

<svelte:window
  on:resize={() => {
    if (resizeTimeout) resizeTimeout();
    resizeTimeout = createTimeout(updateBannerInfo, debounceTime);
  }}
  on:scroll={() => {
    if (scrollTimeout) scrollTimeout();
    scrollTimeout = createTimeout(updateBannerInfo, debounceTime);
  }}
/>

{#if filteredBannerItems && filteredBannerItems.length > 0}
  <div
    transition:slide={{ duration: $reducedMotion ? 0 : 250, axis: 'y' }}
    class={cn('z-50', isBannerSticky ? 'sticky top-0 bg-background' : 'relative')}
    bind:this={ref}
  >
    <div class={cn('mx-auto', !isBannerSticky && 'bg-background')}>
      <div
        class={cn('flex flex-col sm:flex-row gap-2 bg-background items-stretch', className)}
        {...$$restProps}
      >
        <div
          class={cn(
            'flex w-full h-6 flex-col items-center px-2.5',
            hasCountdownItems && 'justify-center'
          )}
        >
          {#each filteredBannerItems as bannerItem, i}
            {@const link = bannerItem?.link ? $getAnchorFromCmsLink(bannerItem?.link) : null}
            {@const campaignColors = bannerItem?.color
              ? getCampaignColors(bannerItem?.color)
              : undefined}
            {@const backgroundColor = campaignColors?.backgroundColor}
            {@const textColor = campaignColors?.textColor}

            <div
              style:--backgroundColor={backgroundColor}
              style:--textColor={textColor}
              class={cn(
                'absolute flex flex-row flex-wrap items-center justify-center gap-x-1 text-primary/80 transition-all motion-reduce:transition-none w-full h-full',
                i === bannerIndex ? 'opacity-100' : 'opacity-0',
                bannerItem?.color ? 'bg-[--backgroundColor]' : 'bg-primary'
              )}
            >
              <Link href={link?.href} target={link?.target}>
                <div class="flex items-center gap-x-1">
                  <p
                    class={cn(
                      'text-xs/none transition-all motion-reduce:transition-none text-highlight text-medium',
                      campaignColors ? 'text-[--textColor]' : 'text-highlight '
                    )}
                  >
                    {bannerItem?.text}
                  </p>
                </div>

                {#if bannerItem?.deadlineFormatted}
                  {@const countdown = bannerItem?.deadlineFormatted}

                  {#if countdown}
                    <div class="flex ml-3">
                      <p class="text-xs/none text-highlight tabular-nums">
                        {#if countdown}
                          {[
                            padNumber(countdown?.hours),
                            padNumber(countdown?.minutes),
                            padNumber(countdown?.seconds)
                          ].join(' : ')}
                        {/if}
                      </p>
                    </div>
                  {/if}
                {/if}
              </Link>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
{/if}
